import React, { FC, useState } from 'react';

import BoxSpoiler from '@app/components/ui/box-spoiler';
import { useTranslation } from '@app/hooks/use-translation';
import { CalculatedRecordMetric, KnownMetricsCases } from '@app/interfaces/records';
import { CircleButton, SimpleDropMenu, Toggle } from '@ui';
import { useAudioContext } from '@app/components/audio-build/record-provider';
import { SettingsMetricType } from '@app/interfaces/pages-types/anatylics-metric.type';

import AccordionItemUI from '@app/components/ui/Accordion/AccordionItem';

import { AccordionItemUIPropsClickChildParams } from '@app/interfaces/userInterface';

import Icon from '@app/components/ui/icons';

import { tableTitleClassName } from './record-metric.style';
import MetricItem from './metric-item';

type RecordMetricProps = {
  projectMetric: CalculatedRecordMetric;
  metricCases?: KnownMetricsCases;
  onChangeActiveMetric: (metricId: string | undefined, type: 'once' | 'all' | 'vad') => void;
  currentTemplate: 'chat' | 'voice';
  defaultStatus?: boolean;
  recordIsCalculated?: boolean;
  handleSetFullTextToSee?(obj: { text: string; name: string }): void;
  handleRecalculateGptGroupResult(gpt_group_id: string): void;
};
const metricTypesToShowCasesOnRecord = [
  'interruptions',
  'pause-exceed',
  'max-pause-duration',
  'max-monolog-duration',
];

const RecordMetric: FC<RecordMetricProps> = (props) => {
  const {
    projectMetric,
    onChangeActiveMetric,
    metricCases,
    currentTemplate,
    defaultStatus = true,
    handleSetFullTextToSee,
    recordIsCalculated,
    handleRecalculateGptGroupResult,
  } = props;
  const { recordId, projectId, gptGroupsList } = useAudioContext();
  const [activeMetricId, setActiveMetric] = useState<string>();
  const { t } = useTranslation('components.audio');
  const [isShowNull, changeShowNull] = useState<boolean>(true);
  const [isShowEmpty, changeShowEmpty] = useState<boolean>(false);
  const [isShowHidden, changeShowHidden] = useState<boolean>(false);
  function handleMetricItemAction({
    caseName,
    value,
  }: {
    caseName: string;
    value: string | number;
  }) {
    if (caseName === 'find-words') {
      const currentMetric = activeMetricId === String(value) ? undefined : String(value);
      setActiveMetric(currentMetric);
      onChangeActiveMetric?.(currentMetric, 'all');
    }
    if (caseName === 'c_find-words') {
      const currentMetric = activeMetricId === String(value) ? undefined : String(value);
      setActiveMetric(currentMetric);
      onChangeActiveMetric?.(currentMetric, 'all');
    }
    if (caseName === 'c_spell-check') {
      const currentMetric = activeMetricId === String(value) ? undefined : String(value);
      setActiveMetric(currentMetric);
      onChangeActiveMetric?.(currentMetric, 'all');
    }
    if (caseName === 'find-words_once') {
      setActiveMetric(value as string);
      onChangeActiveMetric?.(value as string, 'once');
    }
    if (caseName === 'c_find-words_once') {
      setActiveMetric(value as string);
      onChangeActiveMetric?.(value as string, 'once');
    }
    if (caseName === 'c_spell-check_once') {
      setActiveMetric(value as string);
      onChangeActiveMetric?.(value as string, 'once');
    }
    if (metricTypesToShowCasesOnRecord.includes(caseName)) {
      const currentMetric = activeMetricId === String(value) ? undefined : String(value);
      setActiveMetric(currentMetric as string);
      onChangeActiveMetric?.(currentMetric as string, 'vad');
    }
  }

  const renderMetricWithoutGpt = Object.entries(projectMetric)
    .filter(([, knownCase]) => knownCase.type !== 'gpt')
    .filter(([, knownCase]) => {
      if (!isShowNull && knownCase.value === 0) {
        return false;
      }
      return true;
    })
    .filter(([, knownCase]) => {
      if (
        !isShowEmpty &&
        (knownCase.value === '' || knownCase.value === null || knownCase.value === undefined)
      ) {
        return false;
      }
      return true;
    })
    .filter(([, knownCase]) => (isShowHidden ? true : !knownCase.dnt_display))
    .map(([metricId, knownCase], index) => (
      <MetricItem
        isActive={metricId === activeMetricId}
        key={index}
        settings={metricCases?.[metricId]?.known_metric.settings as SettingsMetricType}
        data={knownCase as KnownMetricsCases[string]['known_metric']}
        recordID={recordId}
        projectID={String(projectId)}
        currentTemplate={currentTemplate}
        valueMaxWidth={180}
        onChangeMetricItem={handleMetricItemAction}
      />
    ));

  // ACCORDION
  const [openedAccordion, changeAccordionState] = useState<string[]>([]);

  function handleChangeAccordionState({ id }: AccordionItemUIPropsClickChildParams) {
    changeAccordionState((prev) => {
      return prev.includes(id) ? prev.filter((ids) => ids !== id) : [...prev, id];
    });
  }

  // ACCORDION
  const onlyGptMetric = Object.entries(projectMetric)
    .filter(([, knownCase]) => knownCase.type === 'gpt')
    .filter(([, knownCase]) => (isShowEmpty ? true : !!knownCase.value));

  const renderMetricWithGpt = gptGroupsList?.gpt_groups.map((group) => {
    const isActive = !openedAccordion.includes(group.gpt_group_id);
    return (
      <AccordionItemUI
        key={group.gpt_group_id}
        title={
          <div className="truncate  flex items-center justify-between w-full">
            <span className="truncate max-w-[500px]">{group.name}</span>
            {group.settings.autostart === false &&
              group.gpt_metrics.length > 0 &&
              recordIsCalculated && (
                <div className="flex pt-[2px] w-[23px] h-[20px] items-center justify-center danger ">
                  <CircleButton
                    onClick={() => handleRecalculateGptGroupResult(group.gpt_group_id)}
                    className="text-3color hover:text-action"
                    size={18}
                    icon="RefreshIcon"
                  />
                </div>
              )}
          </div>
        }
        variant="filled"
        clickBtnChild={handleChangeAccordionState}
        active={isActive}
        id={group.gpt_group_id}
      >
        {group.gpt_metrics
          .filter((metric) => (isShowHidden ? true : !metric.dnt_display))
          .map((metric) => {
            const currentMetric = onlyGptMetric.find(([metricId]) => metricId === metric.metric_id);
            if (!currentMetric) return null;
            return (
              <MetricItem
                handleSetFullTextToSee={handleSetFullTextToSee}
                isActive={currentMetric?.[1]?.metric_id === activeMetricId}
                key={currentMetric[0]}
                settings={
                  metricCases?.[currentMetric[0]]?.known_metric.settings as SettingsMetricType
                }
                data={
                  {
                    ...currentMetric[1],
                    result_value_type: metric.result_value_type,
                  } as KnownMetricsCases[string]['known_metric']
                }
                recordID={recordId}
                projectID={String(projectId)}
                currentTemplate={currentTemplate}
                valueMaxWidth={180}
                onChangeMetricItem={handleMetricItemAction}
              />
            );
          })}
      </AccordionItemUI>
    );
  });

  const showMenuHideMetricsDropMenu = (
    <div className="flex flex-col gap-[5px]">
      <div className="flex items-center justify-between">
        <span className=" text-[12px] text-2color ">{t('show_null_values')}</span>
        <Toggle
          size="small"
          onChecked={() => changeShowNull((prevState) => !prevState)}
          checked={isShowNull}
        />
      </div>

      <div className="flex items-center gap-[5px] justify-between">
        <span className=" text-[12px] text-2color ">{t('show_empty_values')}</span>
        <Toggle
          size="small"
          onChecked={() => changeShowEmpty((prevState) => !prevState)}
          checked={isShowEmpty}
        />
      </div>

      <div className="flex items-center gap-[5px] justify-between">
        <span className=" text-[12px] text-2color ">{t('show_hide_values')}</span>
        <Toggle
          size="small"
          onChecked={() => changeShowHidden((prevState) => !prevState)}
          checked={isShowHidden}
        />
      </div>
    </div>
  );

  const renderShowHideMetric = (
    <div className="showHideMenu">
      <SimpleDropMenu menu={showMenuHideMetricsDropMenu}>
        <div className="flex items-center justify-center p-2">
          <Icon name="VerticalDotsIcon" size={13} />
        </div>
      </SimpleDropMenu>
    </div>
  );

  return (
    <BoxSpoiler
      defaultStatus={defaultStatus}
      title={t('metric_list')}
      sideComponent={renderShowHideMetric}
      ignoreSelectors={['.showHideMenu']}
    >
      <div className="grid grid-cols-[1fr_250fr_50px] gap-2 px-[26px]">
        <div className={tableTitleClassName}>{t('metric')}</div>
        <div className={`${tableTitleClassName} text-right`}>{t('metric_value')}</div>
        <div></div>
      </div>
      <div className="py-[15px] pb-[20px] w-full">
        {renderMetricWithoutGpt}
        {renderMetricWithGpt}
      </div>
    </BoxSpoiler>
  );
};

export default RecordMetric;
